import api from './api';

export interface SettingsStatus {
  shop: 'configured' | 'pending' | 'available';
  email: 'configured' | 'pending' | 'available';
  sms: 'configured' | 'pending' | 'available';
  payment: 'configured' | 'pending' | 'available';
  pdf: 'configured' | 'pending' | 'available';
  notifications: 'configured' | 'pending' | 'available';
  landingPage: 'configured' | 'pending' | 'available';
}

class SettingsStatusService {
  /**
   * Check if shop settings are configured
   */
  private async checkShopSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/shop-settings');
      const settings = response.data;

      // Check if basic settings are configured
      if (settings?.companyName && settings?.defaultLaborRate) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check shop settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if email is configured
   */
  private async checkEmailSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/email/config');
      const config = response.data;

      // Check if email has essential configuration
      if (config?.host && config?.hasAuth) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check email settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if SMS is configured
   */
  private async checkSMSSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/sms/config');
      const config = response.data;

      // Check if SMS has essential configuration
      if (config?.accountSid && config?.phoneNumber) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check SMS settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if payment settings are configured
   */
  private async checkPaymentSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/payments/settings');
      const settings = response.data;

      // Check if Stripe or any payment method is configured
      if (settings?.stripePublishableKey || settings?.acceptCash || settings?.acceptChecks) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check payment settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if PDF templates are configured
   */
  private async checkPDFSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/pdf-settings/default');
      const settings = response.data;

      // Check if custom PDF settings exist (not just defaults)
      // If any non-default setting is found, consider it configured
      if (settings && settings.id) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check PDF settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if notification settings are configured
   */
  private async checkNotificationSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/notification-settings');
      const settings = response.data;

      // Check if notification settings exist
      if (settings && (settings.emailRecipients?.length > 0 || settings.smsRecipients?.length > 0)) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check notification settings:', error);
      return 'pending';
    }
  }

  /**
   * Check if landing page is configured
   */
  private async checkLandingPageSettings(): Promise<'configured' | 'pending'> {
    try {
      const response = await api.get('/shop/landing-config');
      const config = response.data;

      // Check if basic info is configured
      if (config?.businessName && config?.phone) {
        return 'configured';
      }
      return 'pending';
    } catch (error) {
      console.error('Failed to check landing page settings:', error);
      return 'pending';
    }
  }

  /**
   * Get status of all settings
   */
  async getAllSettingsStatus(): Promise<SettingsStatus> {
    try {
      // Run all checks in parallel for better performance
      const [shop, email, sms, payment, pdf, notifications, landingPage] = await Promise.all([
        this.checkShopSettings(),
        this.checkEmailSettings(),
        this.checkSMSSettings(),
        this.checkPaymentSettings(),
        this.checkPDFSettings(),
        this.checkNotificationSettings(),
        this.checkLandingPageSettings(),
      ]);

      return {
        shop,
        email,
        sms,
        payment,
        pdf,
        notifications,
        landingPage,
      };
    } catch (error) {
      console.error('Failed to get all settings status:', error);
      // Return default 'pending' status for all on error
      return {
        shop: 'pending',
        email: 'pending',
        sms: 'pending',
        payment: 'pending',
        pdf: 'pending',
        notifications: 'pending',
        landingPage: 'pending',
      };
    }
  }
}

export const settingsStatusService = new SettingsStatusService();
